import { Building2, CircleHelp, LayoutDashboard, Sparkles } from 'lucide-react';

import {
  createTourRegistry,
  defineTour,
  type TourDefinition,
} from '@nucleus-commerce/admin-kit/tour';

import { platformApplicationDetailTour, platformApplicationsListTour } from './applications';
import { platformDiamondsTour, platformExchangeRatesTour } from './catalogue-data';
import { MANAGE } from './manage';
import {
  platformAuditLogTour,
  platformOrganizationActionsTour,
  platformOrganizationDetailTour,
  platformSupportSessionsTour,
} from './organisations';
import {
  platformOperationsTour,
  platformSecurityTour,
  platformSettingsTour,
  platformUsersTour,
} from './platform-team';

export { MANAGE };

/** Help-centre groups, in the order the page shows them; copy at `tours:groups.<key>`. */
export const PLATFORM_TOUR_GROUPS = [
  'getting-started',
  'applications',
  'organisations',
  'catalogue-data',
  'platform-team',
] as const;

export const platformWelcomeTour = defineTour({
  id: 'platform.welcome',
  version: 1,
  group: 'getting-started',
  route: '/',
  scope: 'org',
  icon: Sparkles,
  steps: [
    { id: 'intro', kind: 'intro' },
    {
      id: 'nav',
      kind: 'explain',
      target: 'platform.shell.nav',
      placement: 'right',
      before: { openSidebar: true },
    },
    {
      id: 'applications',
      kind: 'explain',
      target: 'platform.nav.applications',
      placement: 'right',
      before: { openSidebar: true },
    },
    {
      id: 'environment',
      kind: 'explain',
      target: 'platform.shell.environment',
      hideOnMobile: true,
    },
    { id: 'help-menu', kind: 'explain', target: 'platform.shell.help-menu' },
  ],
  related: ['platform.overview', 'platform.applications.list'],
});

export const platformHelpTour = defineTour({
  id: 'platform.help',
  version: 1,
  group: 'getting-started',
  route: '/_authed/help',
  scope: 'org',
  icon: CircleHelp,
  steps: [
    { id: 'intro', kind: 'intro' },
    { id: 'progress', kind: 'explain', target: 'platform.help.progress' },
    { id: 'search', kind: 'action', target: 'platform.help.search', advanceOn: { input: true } },
    { id: 'groups', kind: 'explain', target: 'platform.help.groups', placement: 'top' },
    { id: 'restart', kind: 'explain', target: 'platform.help.restart', optional: true },
  ],
  related: ['platform.welcome'],
});

export const platformOverviewTour = defineTour({
  id: 'platform.overview',
  version: 1,
  group: 'getting-started',
  route: '/',
  scope: 'org',
  icon: LayoutDashboard,
  steps: [
    { id: 'kpis', kind: 'explain', target: 'platform.overview.kpis' },
    { id: 'recent', kind: 'explain', target: 'platform.overview.recent' },
    { id: 'system', kind: 'explain', target: 'platform.overview.system', placement: 'left' },
    { id: 'sales', kind: 'explain', target: 'platform.overview.sales', placement: 'top' },
    { id: 'provision', kind: 'explain', target: 'platform.overview.provision', requires: MANAGE },
  ],
  related: ['platform.organizations.list', 'platform.operations'],
});

export const platformOrganizationsTour = defineTour({
  id: 'platform.organizations.list',
  version: 1,
  group: 'organisations',
  route: '/_authed/organizations/',
  scope: 'org',
  icon: Building2,
  steps: [
    {
      id: 'list',
      kind: 'explain',
      variants: [
        { target: 'platform.organizations.table' },
        { target: 'platform.organizations.empty', copy: 'empty' },
      ],
    },
    { id: 'filters', kind: 'explain', target: 'platform.organizations.filters', optional: true },
    { id: 'search', kind: 'explain', target: 'platform.organizations.search', optional: true },
    {
      id: 'provision',
      kind: 'explain',
      target: 'platform.organizations.provision',
      requires: MANAGE,
    },
  ],
  related: ['platform.organizations.detail'],
});

/** Every platform tour. Add a definition here and its copy to `locales/en/tours.json`. */
export const PLATFORM_TOURS: readonly TourDefinition[] = [
  platformWelcomeTour,
  platformHelpTour,
  platformOverviewTour,
  platformApplicationsListTour,
  platformApplicationDetailTour,
  platformOrganizationsTour,
  platformOrganizationDetailTour,
  platformOrganizationActionsTour,
  platformSupportSessionsTour,
  platformAuditLogTour,
  platformDiamondsTour,
  platformExchangeRatesTour,
  platformOperationsTour,
  platformUsersTour,
  platformSettingsTour,
  platformSecurityTour,
];

export const platformTourRegistry = createTourRegistry(PLATFORM_TOURS);
